import { AxiosError } from 'axios'

export interface FriendlyError {
  title: string
  message: string
  details?: string
}

/**
 * Converts various error types into user-friendly error messages
 */
export function getFriendlyErrorMessage(error: unknown): FriendlyError {
  // Handle Axios errors
  if (isAxiosError(error)) {
    return handleAxiosError(error)
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return {
      title: 'An Error Occurred',
      message: error.message || 'An unexpected error occurred. Please try again.',
      details: error.stack,
    }
  }

  // Handle string errors
  if (typeof error === 'string') {
    return {
      title: 'An Error Occurred',
      message: error,
    }
  }

  // Fallback for unknown error types
  return {
    title: 'Unknown Error',
    message: 'An unexpected error occurred. Please try again later.',
    details: JSON.stringify(error, null, 2),
  }
}

/**
 * Type guard for Axios errors
 */
function isAxiosError(error: unknown): error is AxiosError {
  return (error as AxiosError).isAxiosError === true
}

/**
 * Extracts validation errors from ASP.NET Core problem details response
 */
function extractValidationErrors(responseData: any): string | null {
  if (responseData?.errors && typeof responseData.errors === 'object') {
    const errorMessages: string[] = []

    for (const [field, messages] of Object.entries(responseData.errors)) {
      if (Array.isArray(messages)) {
        // Clean up field names (remove $. prefix, make them readable)
        const cleanField = field.replace(/^\$\./, '').replace(/([A-Z])/g, ' $1').trim()
        messages.forEach((msg: string) => {
          // Simplify technical error messages
          const cleanMsg = msg
            .replace(/Path:.*$/i, '')
            .replace(/LineNumber:.*$/i, '')
            .replace(/BytePositionInLine:.*$/i, '')
            .trim()
          errorMessages.push(`${cleanField}: ${cleanMsg}`)
        })
      }
    }

    return errorMessages.length > 0 ? errorMessages.join('\n') : null
  }

  return null
}

/**
 * Handles Axios-specific errors and converts them to friendly messages
 */
function handleAxiosError(error: AxiosError): FriendlyError {
  // Network error (no response)
  if (!error.response) {
    return {
      title: 'Connection Error',
      message:
        'Unable to connect to the server. Please check your internet connection and try again.',
      details: error.message,
    }
  }

  const status = error.response.status
  const responseData = error.response.data as any

  // Try to extract server-provided error message
  const serverMessage =
    responseData?.message ||
    responseData?.error ||
    responseData?.title ||
    responseData?.Message ||
    responseData?.Error

  // Extract validation errors if present
  const validationErrors = extractValidationErrors(responseData)

  // Map status codes to user-friendly messages
  switch (status) {
    case 400:
      return {
        title: 'Invalid Request',
        message:
          validationErrors ||
          serverMessage ||
          'The information you provided is invalid. Please check your entries and try again.',
        details: JSON.stringify(responseData, null, 2),
      }

    case 401:
      return {
        title: 'Authentication Required',
        message:
          serverMessage ||
          'You need to be logged in to perform this action. Please log in and try again.',
        details: 'HTTP 401 Unauthorized',
      }

    case 403:
      return {
        title: 'Access Denied',
        message:
          serverMessage ||
          'You do not have permission to perform this action. Please contact your administrator if you believe this is an error.',
        details: 'HTTP 403 Forbidden',
      }

    case 404:
      return {
        title: 'Not Found',
        message:
          serverMessage ||
          'The requested resource could not be found. It may have been deleted or moved.',
        details: `HTTP 404 Not Found: ${error.config?.url}`,
      }

    case 409:
      return {
        title: 'Conflict',
        message:
          serverMessage ||
          'This operation conflicts with existing data. The item may have been modified by another user.',
        details: JSON.stringify(responseData, null, 2),
      }

    case 422:
      return {
        title: 'Validation Error',
        message:
          validationErrors ||
          serverMessage ||
          'Some of the information you provided is not valid. Please review your entries and try again.',
        details: JSON.stringify(responseData, null, 2),
      }

    case 429:
      return {
        title: 'Too Many Requests',
        message:
          serverMessage ||
          'You have made too many requests. Please wait a moment and try again.',
        details: 'HTTP 429 Rate Limited',
      }

    case 500:
      return {
        title: 'Server Error',
        message:
          serverMessage ||
          'An internal server error occurred. Our team has been notified. Please try again later.',
        details: 'HTTP 500 Internal Server Error',
      }

    case 502:
      return {
        title: 'Service Unavailable',
        message:
          serverMessage ||
          'The server is temporarily unavailable. Please try again in a few moments.',
        details: 'HTTP 502 Bad Gateway',
      }

    case 503:
      return {
        title: 'Service Unavailable',
        message:
          serverMessage ||
          'The service is temporarily unavailable due to maintenance or high load. Please try again later.',
        details: 'HTTP 503 Service Unavailable',
      }

    case 504:
      return {
        title: 'Request Timeout',
        message:
          serverMessage ||
          'The request took too long to process. Please try again.',
        details: 'HTTP 504 Gateway Timeout',
      }

    default:
      return {
        title: 'Error',
        message:
          serverMessage ||
          `An unexpected error occurred (Error ${status}). Please try again later.`,
        details: JSON.stringify(
          {
            status,
            statusText: error.response.statusText,
            data: responseData,
          },
          null,
          2
        ),
      }
  }
}

/**
 * Special handling for save/update operations
 */
export function getSaveFriendlyError(error: unknown): FriendlyError {
  const baseError = getFriendlyErrorMessage(error)

  // Customize title for save operations
  if (baseError.title === 'An Error Occurred') {
    baseError.title = 'Save Failed'
  }

  // Enhance message for common save scenarios
  if (isAxiosError(error) && error.response) {
    const status = error.response.status

    if (status === 400 || status === 422) {
      baseError.message =
        'Unable to save your changes. Please check that all required fields are filled in correctly and try again.'
    } else if (status === 409) {
      baseError.message =
        'Unable to save because another user has modified this adventure. Please refresh and try again.'
    } else if (status === 413) {
      baseError.title = 'Content Too Large'
      baseError.message =
        'Your adventure content is too large to save. Please reduce the size of images or descriptions and try again.'
    }
  }

  return baseError
}
